/**
 * Liste des dossiers · Sprint V2 · wyvio-ui
 * ─────────────────────────────────────────
 * Mission de la page : "Quel dossier dois-je ouvrir ?"
 *
 * On répond en <3s avec :
 *   - un salut silencieux (context : combien de dossiers à traiter)
 *   - des cartes produit véhiculo-centrées (photo, nom, client, étape, action)
 *   - 5 filtres simples : À traiter · En cours · À livrer · Bloqués · Terminés
 *   - une recherche unique par vehicule/client/BC
 *
 * Aucun autre composant. Tout provient de `@/components/wyvio-ui`.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import DossierForm from "@/components/dossier/DossierForm";
import { normalizeListItem } from "@/lib/normalize";
import {
  Button, Card, RowCard, Toolbar, SegmentedControl,
  MicroLabel, Chip, VehicleThumb, StatusDot, Avatar,
} from "@/components/wyvio-ui";

// ────────────────────────────────────────────────────────────────────────
// Barre de progression 13 segments (fallback quand steps absent)
// ────────────────────────────────────────────────────────────────────────
const ProgressBar13 = ({ progress = 0, blocked = false }) => {
  const filled = Math.round((13 * (progress || 0)) / 100);
  return (
    <div className="flex items-center gap-[3px]" aria-label={`Progression ${progress}%`}>
      {Array.from({ length: 13 }).map((_, i) => {
        const isFilled = i < filled;
        const isTip = i === filled - 1;
        const cls = blocked && isTip
          ? "bg-[#EF4444]"
          : isTip
          ? "bg-[#C8FF2F]"
          : isFilled
          ? "bg-[#0A0A0A] dark:bg-white"
          : "bg-[#EEF2F5] dark:bg-[#262626]";
        return <span key={i} className={`h-[3px] flex-1 rounded-full ${cls}`} />;
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Filtres métier — mission de la page
// ────────────────────────────────────────────────────────────────────────
const FILTERS = [
  { key: "attention", label: "À traiter" },
  { key: "progress",  label: "En cours" },
  { key: "delivery",  label: "À livrer" },
  { key: "blocked",   label: "Bloqués" },
  { key: "done",      label: "Terminés" },
];

const matchesFilter = (d, filter, query) => {
  if (query) {
    const q = query.toLowerCase();
    const hay = [d.name, d.client, d.orderNumber, d.commercial]
      .filter(Boolean).join(" ").toLowerCase();
    if (!hay.includes(q)) return false;
  }
  switch (filter) {
    case "blocked":   return d.currentStatus?.color === "red";
    case "done":      return d.isFinished;
    case "delivery":  return !d.isFinished && d.progress >= 70;
    case "progress":  return !d.isFinished && d.progress > 0 && d.progress < 70;
    case "attention":
    default:
      return !d.isFinished && (d.currentStatus?.color === "red"
                             || d.currentStatus?.color === "orange"
                             || d.progress < 10);
  }
};

// ────────────────────────────────────────────────────────────────────────
// Carte dossier (produit)
// ────────────────────────────────────────────────────────────────────────
const DossierCard = ({ v, onOpen }) => {
  const blocked = v.currentStatus?.color === "red";
  return (
    <Card
      interactive
      onClick={onOpen}
      data-testid={`dossier-card-${v.id}`}
      className="p-0 overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8FF2F]"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
    >
      {/* Photo véhicule 16:9 (ADN) */}
      <div className="relative aspect-[16/9] w-full overflow-hidden">
        <VehicleThumb src={v.image} alt={v.name} size={9999} className="w-full h-full !rounded-none !border-0" />
        {blocked && (
          <span className="absolute top-3 left-3">
            <Chip tone="danger" size="sm" uppercase>Bloqué</Chip>
          </span>
        )}
      </div>

      {/* Contenu texte */}
      <div className="px-5 pt-5 pb-5">
        <div className="flex items-baseline justify-between gap-3 min-w-0">
          <h3 className="text-[15.5px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.01em] truncate">
            {v.name}
          </h3>
          <span className="shrink-0 text-[10.5px] font-medium text-[#9CA3AF] dark:text-[#525252] tabular-nums">
            {v.orderNumber}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[12px] text-[#6B7280] dark:text-[#A3A3A3] min-w-0">
          <Avatar name={v.client} size={6} />
          <span className="truncate">{v.client || "—"}</span>
        </div>

        <div className="mt-5">
          <ProgressBar13 progress={v.progress} blocked={blocked} />
          <div className="mt-2.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <StatusDot tone={blocked ? "danger" : v.currentStatus?.color === "orange" ? "warn" : "accent"} />
              <span className="text-[11.5px] font-medium text-[#0A0A0A] dark:text-white truncate">
                {v.currentStatus?.label || "—"}
              </span>
            </div>
            <span className="text-[11px] font-medium text-[#9CA3AF] dark:text-[#525252] tabular-nums">
              {v.progress || 0}%
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Main
// ────────────────────────────────────────────────────────────────────────
const DossierList = () => {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("attention");
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const list = await api.listDossiers();
      setDossiers(list.map(normalizeListItem));
      setError(null);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    dossiers.filter((d) => matchesFilter(d, filter, query)),
    [dossiers, filter, query],
  );

  // Compteurs par filtre (informatifs) — non affichés dans la UI mais utiles au segmented
  return (
    <div
      className="-mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-[calc(100vh-0px)] bg-[#F7F8FA] dark:bg-[#080808] transition-colors"
      data-testid="page-dossier-list"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-32">

        {/* Toolbar */}
        <Toolbar
          className="mb-16"
          left={
            <div className="relative">
              <Search
                size={13}
                strokeWidth={2}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] dark:text-[#737373] pointer-events-none"
              />
              <input
                type="text"
                data-testid="dossier-list-search"
                placeholder="Rechercher véhicule, client, BC…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 pl-9 pr-4 rounded-full text-[12.5px]
                           bg-white dark:bg-[#111111]
                           border border-[#EEF2F5] dark:border-[#262626]
                           text-[#0A0A0A] dark:text-white
                           placeholder:text-[#9CA3AF] dark:placeholder:text-[#525252]
                           focus:outline-none focus:border-[#DCE1E7] dark:focus:border-[#3A3A3A]
                           transition-all duration-200 w-[280px]"
              />
            </div>
          }
          right={
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowForm(true)}
              data-testid="dossier-list-new"
            >
              Nouveau dossier
            </Button>
          }
        />

        {/* Hero + filtres */}
        <section className="mb-14" data-testid="dossier-list-hero">
          <MicroLabel>Portefeuille</MicroLabel>
          <h1 className="mt-4 text-[42px] sm:text-[56px] lg:text-[64px] leading-[0.98] font-semibold tracking-[-0.045em] text-[#0A0A0A] dark:text-white">
            {loading
              ? "Vos dossiers."
              : filtered.length === 0
              ? "Aucun dossier."
              : `${filtered.length} dossier${filtered.length > 1 ? "s" : ""}.`}
          </h1>
          <p className="mt-5 text-[16px] sm:text-[19px] leading-[1.4] text-[#6B7280] dark:text-[#A3A3A3] max-w-xl">
            {filter === "attention" && "Ceux qui demandent votre attention."}
            {filter === "progress" && "En cours de traitement."}
            {filter === "delivery" && "Prêts à livrer prochainement."}
            {filter === "blocked" && "Nécessitent une action pour repartir."}
            {filter === "done" && "Ventes finalisées."}
          </p>

          <div className="mt-8 overflow-x-auto no-scrollbar">
            <SegmentedControl
              testId="dossier-list-filters"
              value={filter}
              onChange={setFilter}
              options={FILTERS}
            />
          </div>
        </section>

        {/* Grille */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="wyvio-card overflow-hidden animate-pulse">
                <div className="aspect-[16/9] w-full bg-[#EEF2F5] dark:bg-[#1A1A1A]" />
                <div className="p-5 space-y-3">
                  <div className="h-3.5 w-2/3 bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded" />
                  <div className="h-3 w-1/2 bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded" />
                  <div className="h-1 w-full bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <RowCard className="p-6 text-center text-[13px] text-[#991B1B]" interactive={false}>
            {error}
          </RowCard>
        ) : filtered.length === 0 ? (
          <Card
            interactive={false}
            className="p-16 text-center border-dashed"
            data-testid="dossier-list-empty"
          >
            <p className="text-[15px] font-semibold text-[#0A0A0A] dark:text-white tracking-tight">
              Rien à afficher.
            </p>
            <p className="mt-2 text-[13px] text-[#9CA3AF] dark:text-[#737373]">
              {query
                ? "Aucun dossier ne correspond à cette recherche."
                : "Ajustez le filtre ou créez un nouveau dossier."}
            </p>
            {!query && (
              <div className="mt-6">
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => setShowForm(true)}
                >
                  Nouveau dossier
                </Button>
              </div>
            )}
          </Card>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="dossier-list-grid"
          >
            {filtered.map((v) => (
              <DossierCard
                key={v.id}
                v={v}
                onOpen={() => navigate(`/app/dossier/${v.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <DossierForm
          mode="create"
          onClose={() => setShowForm(false)}
          onSaved={(created) => {
            if (created?.id) navigate(`/app/dossier/${created.id}`);
            else load();
          }}
        />
      )}
    </div>
  );
};

export default DossierList;
